import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Bidirectional mapping between a typed filter state and the URL query
 * string. Define ONE codec per page as a module-level constant (a stable
 * identity is required — the hook memoises on it).
 *
 * - `parse` must be total: unknown / malformed / out-of-vocabulary params
 *   are dropped (never thrown, never trusted) and defaults applied.
 * - `write` receives a COPY of the current params. It must delete every key
 *   the codec owns and re-set the ones present in `value`, leaving foreign
 *   params untouched. Values equal to the default should be omitted so
 *   URLs stay short and a pristine page has a clean address.
 */
export interface UrlFilterCodec<T> {
  parse: (sp: URLSearchParams) => T
  write: (value: T, sp: URLSearchParams) => void
}

/**
 * Makes the URL the single source of truth for page filter state (so deep
 * links from dashboards work, refresh and back/forward are faithful, and
 * there is no local-state-vs-URL sync loop). Writes use `replace` so that
 * every filter tweak does not pollute browser history.
 *
 * The setter accepts a value or an updater. Updaters see the result of any
 * earlier write in the same tick, so two writes in one handler compose.
 */
export function useUrlFilters<T>(
  codec: UrlFilterCodec<T>,
): readonly [T, (next: T | ((prev: T) => T)) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  // `searchParams` identity changes on every navigation; key on content.
  const serialized = searchParams.toString()
  const value = useMemo(
    () => codec.parse(new URLSearchParams(serialized)),
    [codec, serialized],
  )

  // Functional form: React Router hands the updater the latest committed
  // params, and we additionally track writes made earlier in the same tick
  // (before a re-render) so two setValue calls in one handler compose.
  const pending = useRef<{ base: string; latest: string } | null>(null)
  useEffect(() => {
    pending.current = null // URL committed; the router's params are authoritative again
  }, [serialized])
  const setValue = useCallback(
    (next: T | ((prev: T) => T)) => {
      setSearchParams(
        (prev) => {
          const base = prev.toString()
          const current =
            pending.current && pending.current.base === base ? pending.current.latest : base
          const resolved =
            typeof next === 'function'
              ? (next as (p: T) => T)(codec.parse(new URLSearchParams(current)))
              : next
          const copy = new URLSearchParams(current)
          codec.write(resolved, copy)
          const out = copy.toString()
          pending.current = { base, latest: out }
          // No-op writes must not create history entries.
          return out === base ? prev : copy
        },
        { replace: true },
      )
    },
    [codec, setSearchParams],
  )

  return [value, setValue] as const
}
